import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from 'recharts';

export const ROASOverTimeChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();

        return sortedDates.map((date) => {
            const day = finance.dailyBreakdown![date];
            const revenue = Number(day.metrics?.revenue) || 0;
            const adSpend = Number(day.metrics?.adSpend) || 0;
            const roas = adSpend > 0 ? Math.round((revenue / adSpend) * 100) / 100 : 0;

            return {
                date,
                label: format(parseISO(date), 'MMM d'),
                roas,
            };
        });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="label"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => `${value}x`}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                `${value}x`,
                                t('charts.roas', 'ROAS'),
                            ]}
                        />
                        <ReferenceLine
                            y={1}
                            stroke={CHART_COLORS.gray[400]}
                            strokeDasharray="4 4"
                            label={{
                                value: t('charts.breakEven', 'Break-even'),
                                position: 'right',
                                fill: CHART_COLORS.gray[500],
                                fontSize: 10,
                            }}
                        />
                        <Line
                            type="monotone"
                            dataKey="roas"
                            stroke={CHART_COLORS.profit}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            dot={CHART_STYLES.line.dot}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.profit }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
